import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, Dropdown, DropdownButton, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { FilterActions } from '../../../shared/components/FilterActions';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';
import CustomFancybox from '../../../shared/components/Fancybox';
import { ContractPdfService } from '../ContractPdfService';
import { formatDate, getCorrectEnums, hasAccessRight } from '../../../utils/Functions';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import {
    ContractPdf,
    ContractPdfCampaignAutocomplete,
    ContractPdfUserAutocomplete,
} from '../../../model/contract/ContractPdf';
import { OfferPdfTypeEnum } from '../../../model/offer/OfferPdf';

const initialFilters = {
    page: 1,
    perPage: 15,
    contractId: '',
    campaignId: '',
    userId: '',
    sentById: '',
    type: '',
    totalPages: 0,
    isCurrent: '',
};

const CONTRACT_PDF_COLUMNS: ITableColumn[] = [
    { label: 'Date' },
    { label: 'Contract ID' },
    { label: 'Campaign' },
    { label: 'Influencer' },
    { label: 'Sent by' },
    { label: 'Type' },
    { label: 'Current?' },
    { label: 'Action' },
];

export function ContractPdfList() {
    const [dataLoading, setDataLoading] = useState(true);
    const [contractPdfs, setContractPdfs] = useState<ContractPdf[]>([]);
    const [filters, setFilters] = useState(initialFilters);
    const [resetKey, setResetKey] = useState(0);
    const [viewingPdfId, setViewingPdfId] = useState<number | null>(null);
    const [regeneratingId, setRegeneratingId] = useState<number | null>(null);

    const [campaignFilter, setCampaignFilter] = useState('');
    const [influencerFilter, setInfluencerFilter] = useState('');
    const [sentByFilter, setSentByFilter] = useState('');

    const [campaignOptions, setCampaignOptions] = useState<ContractPdfCampaignAutocomplete[]>([]);
    const [influencerOptions, setInfluencerOptions] = useState<ContractPdfUserAutocomplete[]>([]);
    const [sentByOptions, setSentByOptions] = useState<ContractPdfUserAutocomplete[]>([]);

    useEffect(() => {
        fetchContractPdfs(filters);
    }, []);

    useEffect(() => {
        ContractPdfService.getCampaignsAutocomplete(campaignFilter).then(setCampaignOptions).catch(ErrorToast);
    }, [campaignFilter]);

    useEffect(() => {
        ContractPdfService.getInfluencersAutocomplete(influencerFilter).then(setInfluencerOptions).catch(ErrorToast);
    }, [influencerFilter]);

    useEffect(() => {
        ContractPdfService.getSentByAutocomplete(sentByFilter).then(setSentByOptions).catch(ErrorToast);
    }, [sentByFilter]);

    const fetchContractPdfs = (params: typeof filters, pageOverride?: number) => {
        setDataLoading(true);
        const page = pageOverride ?? params.page;

        ContractPdfService.getContractPdfs({ ...params, page })
            .then(({ data, page: responsePage, count }) => {
                const totalPages = Math.ceil(count / params.perPage);

                setContractPdfs(data);
                setFilters(() => ({
                    ...params,
                    page: responsePage,
                    totalPages,
                }));
            })
            .catch(ErrorToast)
            .finally(() => setDataLoading(false));
    };

    const handlePageChange = (page: number) => {
        fetchContractPdfs(filters, page);
    };

    const handleChange = (evt: React.ChangeEvent<any>) => {
        const { name, value } = evt.target;
        setFilters(prev => ({ ...prev, page: 1, [name]: value }));
    };

    const handleSearch = () => {
        fetchContractPdfs({ ...filters, page: 1 });
    };

    const handleClear = () => {
        setFilters(initialFilters);
        setCampaignFilter('');
        setInfluencerFilter('');
        setSentByFilter('');
        setResetKey(prev => prev + 1);
        fetchContractPdfs(initialFilters, 1);
    };

    const hasActiveFilters =
        !!filters.contractId ||
        !!filters.campaignId ||
        !!filters.userId ||
        !!filters.sentById ||
        !!filters.type ||
        !!filters.isCurrent;

    const handleRegenerate = (id: number) => {
        setRegeneratingId(id);
        ContractPdfService.regeneratePdf(id)
            .then(() => {
                SuccessToast('PDF regenerated successfully');
                fetchContractPdfs(filters);
            })
            .catch(ErrorToast)
            .finally(() => setRegeneratingId(null));
    };

    const renderStatusIcon = (condition?: boolean) =>
        condition ? (
            <span className="word-break text-primary">
                <FaCheck />
            </span>
        ) : (
            <span className="word-break text-danger">
                <FaTimes />
            </span>
        );

    const renderWithTooltip = (text?: string) =>
        text ? (
            <OverlayTrigger placement="top" overlay={<Tooltip id="contract-pdf-campaign-tooltip">{text}</Tooltip>}>
                <span className="ml-1">
                    <FiInfo size={14} className="cursor-pointer" />
                </span>
            </OverlayTrigger>
        ) : null;

    return (
        <>
            <Card
                className="mb-5 p-4"
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') handleSearch();
                }}
            >
                <Row key={resetKey} style={{ rowGap: '18px' }}>
                    <div className="col-md-2 col-6">
                        <Form.Control
                            placeholder="Contract ID"
                            className="autocomplete-input"
                            name="contractId"
                            value={filters.contractId}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Campaign name or ID"
                            onSelect={setFilters}
                            setFilter={setCampaignFilter}
                            keyState={'campaignId'}
                            keyItemSelect={'id'}
                            value={campaignFilter}
                            items={campaignOptions}
                            hasClearBtn
                            renderItems={['id', 'title']}
                            clearFunc={() => {
                                setFilters(prev => ({ ...prev, campaignId: '' }));
                                setCampaignFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Influencer name or ID"
                            onSelect={setFilters}
                            setFilter={setInfluencerFilter}
                            keyState={'userId'}
                            keyItemSelect={'id'}
                            value={influencerFilter}
                            items={influencerOptions}
                            hasClearBtn
                            renderItems={['id', 'displayName']}
                            clearFunc={() => {
                                setFilters(prev => ({ ...prev, userId: '' }));
                                setInfluencerFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Sent by (name or ID)"
                            onSelect={setFilters}
                            setFilter={setSentByFilter}
                            keyState={'sentById'}
                            keyItemSelect={'id'}
                            value={sentByFilter}
                            items={sentByOptions}
                            hasClearBtn
                            renderItems={['id', 'firstName', 'lastName']}
                            clearFunc={() => {
                                setFilters(prev => ({ ...prev, sentById: '' }));
                                setSentByFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="All types"
                            options={[
                                { value: OfferPdfTypeEnum.INFLUENCER, label: 'Influencer' },
                                { value: OfferPdfTypeEnum.BRAND, label: 'Brand' },
                            ]}
                            selected={String(filters?.type ?? '')}
                            onChange={value => handleChange({ target: { name: 'type', value } } as any)}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="is Current?"
                            options={[
                                { value: 'true', label: 'Current contract' },
                                { value: 'false', label: 'Not current contract' },
                            ]}
                            selected={String(filters?.isCurrent ?? '')}
                            onChange={value => handleChange({ target: { name: 'isCurrent', value } } as any)}
                        />
                    </div>
                </Row>
                <FilterActions onSearch={handleSearch} onClear={handleClear} hasActiveFilters={hasActiveFilters} />
            </Card>

            <TableLayout
                pagination={filters}
                listLength={contractPdfs.length}
                handlePages={handlePageChange}
                isLoading={dataLoading}
                dataName={'contract pdfs'}
                skeletonColumns={CONTRACT_PDF_COLUMNS}
                skeletonRows={filters.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {CONTRACT_PDF_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {contractPdfs.map(contractPdf => (
                            <tr key={contractPdf.id}>
                                <td>{formatDate(contractPdf.createdAt)}</td>
                                <td>{contractPdf?.contract?.id}</td>
                                <td>
                                    {contractPdf?.contract?.campaign?.id}
                                    {renderWithTooltip(contractPdf?.contract?.campaign?.title)}
                                </td>
                                <td>
                                    <ImpersonateEye id={contractPdf?.contract?.user?.id ?? 0} />
                                    {contractPdf?.contract?.user?.displayName}
                                </td>
                                <td>
                                    <ImpersonateEye id={contractPdf?.sentBy?.id ?? 0} />
                                    {contractPdf?.sentBy?.firstName} {contractPdf?.sentBy?.lastName}
                                </td>
                                <td>{getCorrectEnums(contractPdf?.type)}</td>
                                <td>{renderStatusIcon(contractPdf?.contract?.isCurrent)}</td>
                                <td>
                                    <DropdownButton title="Actions" id={`contract-pdf-actions-${contractPdf.id}`}>
                                        <Dropdown.Item onClick={() => setViewingPdfId(contractPdf.id)}>
                                            View
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => window.open(contractPdf.path, '_blank')}>
                                            Download
                                        </Dropdown.Item>
                                        {hasAccessRight(AdminAclEnum.REGENERATE_PDF) && (
                                            <Dropdown.Item
                                                disabled={regeneratingId === contractPdf.id}
                                                onClick={() => handleRegenerate(contractPdf.id)}
                                            >
                                                {regeneratingId === contractPdf.id
                                                    ? 'Regenerating...'
                                                    : 'Regenerate PDF'}
                                            </Dropdown.Item>
                                        )}
                                    </DropdownButton>
                                    <CustomFancybox
                                        files={[contractPdf]}
                                        isView
                                        hideTrigger
                                        open={viewingPdfId === contractPdf.id}
                                        onClose={() => setViewingPdfId(null)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
